import { useCallback, useDeferredValue, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { PeriodTabs } from '../components/PeriodTabs';
import { RaporTablosu } from '../components/rapor-ekrani/RaporTablosu';
import { RaporHatasi } from '../components/rapor-ekrani/RaporHatasi';
import { FILTRE_GIRIS, FiltreAlani, RaporFiltreBari } from '../components/rapor-ekrani/RaporFiltreBari';
import { useDoluYukseklik } from '../components/rapor-ekrani/useDoluYukseklik';
import { useCekmeceSecimi } from '../components/rapor-ekrani/useCekmeceSecimi';
import { ekranKaynagi, useRaporSorgu } from '../components/rapor-ekrani/useRaporSorgu';
import { useDarEkran } from '../components/rapor-ekrani/useDarEkran';
import { kucukTr } from '../components/rapor-ekrani/bicim';
import type { RaporKolonu } from '../components/rapor-ekrani/kolon';
import { GruplamaAlanlari, LokasyonSecimi, UrunGrubuSecimi } from '../components/stok-rapor/StokSuzgecleri';
import { StokDegerCekmecesi } from '../components/stok-rapor/StokCekmeceleri';
import { useGruplama } from '../components/stok-rapor/useGruplama';
import { useLokasyonSecimi } from '../components/stok-rapor/useLokasyonSecimi';
import {
  DEGERLEME_ADI, DEGERLEME_SECENEKLERI, DURUM_SECENEKLERI, birimMaliyet, grupKolonlari, grupSayisi, gruplamaEtiketi,
  stokDegerSatiri, stokDegerSuz, stokDegeri, urunGruplari,
  type Degerleme, type HamStokDegerSatiri, type StokDegerSatiri, type StokDurumu,
} from '../components/stok-rapor/tipler';
import type { EkranProps } from '../ekranKayitDefteri';

const SAYI = new Intl.NumberFormat('tr-TR');

/**
 * S345: Stok Değer Raporu (ERP12 rapor ekranları; onaylı demo `tasks/s334/erp12-rapor-demosu.html`, `deger`).
 * Liste `liste` ds'inden (satır = ürün), satıra basınca açılan çekmece `lokasyon` ve `fisler` ds'lerinden
 * beslenir; kaynaklar `firm_panel_screens.config.kaynaklar` sözlüğünden çözülür.
 *
 * Durum, değerleme, ürün grubu, arama ve gruplama İSTEMCİDE çalışır (sorgu yinelenmez); lokasyon
 * değişince liste yeniden sorgulanır ve önceki liste o arada ekranda kalır. Değer = eldeki
 * miktar × seçili maliyet, satır satır kuruşa yuvarlı. Gruplama seçenekleri ve Ürün Grubu / Marka /
 * Tedarikçi kolonları yalnız firmanın verisinde dolu olan alanlardır. Çekmecedeki ürün URL'de durur
 * (`?stok=<id>`).
 *
 * LOKASYON FİLTRESİ (S349): seçenekler `lokasyonlar` ds'inden gelir (`lokasyon` ds'i tek ürünün dağılımıdır,
 * seçim listesi değil). Seçilince liste ve çekmecedeki son çıkış hareketleri `@LOKASYON` ile sorgulanır;
 * "Tümü"de parametre gönderilmez (ds boş değeri "tümü" sayar). Çekmecedeki lokasyon dağılımı her zaman TÜM
 * lokasyonlardır. `lokasyonlar` kaynağı sözlükte yoksa, hata verirse ya da boşsa filtre çizilmez.
 *
 * TELEFON (<= 767 px): liste KART (üstte ürün adı, sağında değer; altında kod, son alış tarihi, eldeki
 * miktar, birim maliyet). Yapışık barda yalnız durum seçicisi; ötekiler katlanır "Filtreler"in içinde.
 */
export default function StokDegerRaporu({ screen }: EkranProps) {
  const listeKaynagi = ekranKaynagi(screen, 'liste');
  const lokasyonKaynagi = ekranKaynagi(screen, 'lokasyon');
  const fislerKaynagi = ekranKaynagi(screen, 'fisler');
  const telefon = useDarEkran();
  const doluRef = useDoluYukseklik<HTMLDivElement>(telefon ? 'enAz' : 'yukseklik');

  const [durum, setDurum] = useState<StokDurumu>('arti');
  const [degerleme, setDegerleme] = useState<Degerleme>('son');
  const [urunGrubuSecimi, setUrunGrubu] = useState('');
  const [ara, setAra] = useState('');
  const [bugun] = useState(() => dayjs().format('YYYY-MM-DD'));

  const lokasyon = useLokasyonSecimi(screen);

  const q = useRaporSorgu<HamStokDegerSatiri>(listeKaynagi, { '@LOKASYON': lokasyon.id });
  const tum = useMemo(() => (q.data ?? []).map(stokDegerSatiri), [q.data]);
  const gruplar = useMemo(() => urunGruplari(tum), [tum]);
  const urunGrubu = gruplar.includes(urunGrubuSecimi) ? urunGrubuSecimi : '';
  // Arama tuş başına on binlerce satırı süzer: giriş alanı akıcı kalsın diye süzme ertelenir.
  const araGecikmeli = useDeferredValue(ara);
  const suzulen = useMemo(() => stokDegerSuz(tum, durum, urunGrubu, araGecikmeli), [tum, durum, urunGrubu, araGecikmeli]);
  // Eldeki miktarı 0 olan ürün hiçbir durumda listede yok: "süzüldü" kararına taban olmaz.
  const stokluSayisi = useMemo(() => tum.filter((s) => s.elde !== 0).length, [tum]);

  const gruplama = useGruplama<StokDegerSatiri>(tum, 'urun_grubu');
  const { dolu } = gruplama;
  const grupAdedi = useMemo(() => grupSayisi(suzulen, gruplama.alan), [suzulen, gruplama.alan]);

  const kolonlar = useMemo<RaporKolonu<StokDegerSatiri>[]>(() => [
    { id: 'stok_kodu', baslik: 'Stok Kodu', tur: 'kod', deger: (s) => s.kod, kart: 'alt' },
    { id: 'urun', baslik: 'Ürün', tur: 'metin', deger: (s) => s.ad, esnek: true, kart: 'baslik' },
    ...grupKolonlari<StokDegerSatiri>(dolu),
    { id: 'eldeki_miktar', baslik: 'Eldeki Miktar', tur: 'sayi', deger: (s) => s.elde, toplam: true, kart: 'ikincil' },
    { id: 'birim_maliyet', baslik: 'Birim Maliyet', tur: 'para', deger: (s) => birimMaliyet(s, degerleme), kart: 'ikincil' },
    {
      id: 'toplam_deger', baslik: 'Toplam Değer', tur: 'para', toplam: true, kart: 'olcu',
      deger: (s) => stokDegeri(s.elde, birimMaliyet(s, degerleme)),
    },
    { id: 'son_alis_tarihi', baslik: 'Son Alış Tarihi', tur: 'tarih', deger: (s) => s.sonAlisTarihi, kart: 'alt', kartEtiketi: 'Son alış' },
  ], [dolu, degerleme]);

  const secim = useCekmeceSecimi('stok');
  const seciliStok = useMemo(
    () => (secim.secili ? tum.find((s) => s.id === secim.secili) : undefined),
    [tum, secim.secili],
  );
  const { ac } = secim;
  const satirTiklandi = useCallback((s: StokDegerSatiri) => ac(s.id), [ac]);

  const ilkYukleme = q.isLoading;
  const yenileniyor = q.isFetching && !ilkYukleme;

  const aramaGirisi = (
    <input
      // Telefonda `search` DEĞİL `text`: iOS `search` girdisine 24 px'lik kendi temizleme düğmesini koyar.
      type={telefon ? 'text' : 'search'}
      inputMode="search"
      className={telefon ? `${FILTRE_GIRIS} w-full` : `${FILTRE_GIRIS} w-[220px] max-w-full`}
      placeholder="Stok kodu ya da ürün"
      aria-label={telefon ? 'Stok kodu ya da ürün adında ara' : undefined}
      value={ara}
      onChange={(e) => setAra(e.target.value)}
    />
  );
  const grupNotu = gruplama.alan === '' ? '' : `${SAYI.format(grupAdedi)} ${kucukTr(gruplamaEtiketi(gruplama.alan))}`;
  const degerNotu = `değer = eldeki miktar × ${DEGERLEME_ADI[degerleme]} (KDV hariç)`;
  const etkinAdlar = [lokasyon.id !== '' && 'Lokasyon', urunGrubu !== '' && 'Ürün grubu'].filter((x): x is string => !!x);

  return (
    <div
      ref={doluRef}
      data-testid="stok-deger-raporu"
      className={telefon ? 'flex flex-col gap-2 -mb-4 rk-kenar-16' : 'flex flex-col gap-2 min-h-[320px]'}
    >
      <RaporFiltreBari
        testid="stok-deger-filtre-bar"
        birincil={
          <FiltreAlani etiket="Durum" grup>
            <PeriodTabs value={durum} onChange={setDurum} secenekler={DURUM_SECENEKLERI} kompakt />
          </FiltreAlani>
        }
        etkinAdlar={etkinAdlar}
        ozet={ilkYukleme || !listeKaynagi ? undefined : `${SAYI.format(suzulen.length)} ürün · ${DEGERLEME_ADI[degerleme]}`}
      >
        {lokasyon.secenekler.length > 0 && <LokasyonSecimi deger={lokasyon.id} lokasyonlar={lokasyon.secenekler} onDegis={lokasyon.sec} />}
        <FiltreAlani etiket="Değerleme" grup>
          <PeriodTabs value={degerleme} onChange={setDegerleme} secenekler={DEGERLEME_SECENEKLERI} kompakt />
        </FiltreAlani>
        {gruplar.length > 0 && <UrunGrubuSecimi deger={urunGrubu} gruplar={gruplar} onDegis={setUrunGrubu} />}
        {!telefon && <FiltreAlani etiket="Ara">{aramaGirisi}</FiltreAlani>}
        <GruplamaAlanlari alan={gruplama.alan} dolu={dolu} onSec={gruplama.sec} onAciklik={gruplama.setAciklik} />
      </RaporFiltreBari>

      {/* Telefonda bu satır yok: özet "Filtreler" başlığında (sayı ekranda tek yerde). */}
      {!telefon && (
        <div className="text-[13px] text-ink-500 min-h-[20px]" data-testid="stok-deger-bilgi">
          {ilkYukleme || !listeKaynagi ? (
            <>&nbsp;</>
          ) : (
            <>
              <b className="font-semibold text-ink-900">{SAYI.format(suzulen.length)}</b> ürün
              {grupNotu && <> · {grupNotu} (kartta boş olanlar "(Tanımsız)" grubunda)</>}
              {' · '}{degerNotu}
              {lokasyon.ad && <> · {lokasyon.ad}</>}
              {yenileniyor && <span className="ml-2 text-ink-400" role="status">Yenileniyor…</span>}
            </>
          )}
        </div>
      )}

      {!listeKaynagi ? (
        <RaporHatasi testid="stok-deger-kaynaksiz" mesaj="Bu firmada stok değer raporu kaynağı tanımlı değil." />
      ) : q.isError ? (
        <RaporHatasi testid="stok-deger-hata" mesaj="Stok değer raporu yüklenemedi." onTekrar={() => q.refetch()} />
      ) : (
        <RaporTablosu
          testid="stok-deger-tablosu"
          etiket="Stok Değer Raporu"
          kolonlar={kolonlar}
          satirlar={suzulen}
          satirKimligi={(s) => s.id}
          onSatirTikla={satirTiklandi}
          seciliKimlik={seciliStok?.id ?? null}
          grup={gruplama.grup}
          grupAcikligi={gruplama.aciklik}
          onGrupAcikligi={gruplama.setAciklik}
          hamSatirSayisi={stokluSayisi}
          // Lokasyon satır sayısını azaltmadan da toplamı daraltır (ürünün yalnız o lokasyondaki miktarı kalır).
          suzuldu={lokasyon.id !== ''}
          bosMesaj={tum.length === 0 ? 'Stok kartı yok.' : 'Bu süzgeçle kayıt yok.'}
          yukleniyor={ilkYukleme}
          yenileniyor={yenileniyor}
          kartAraclari={aramaGirisi}
        />
      )}

      <StokDegerCekmecesi
        stok={seciliStok}
        degerleme={degerleme}
        lokasyonKaynagi={lokasyonKaynagi}
        fislerKaynagi={fislerKaynagi}
        lokasyon={lokasyon.id}
        bugun={bugun}
        onKapat={secim.kapat}
      />
    </div>
  );
}
